import { useEffect, useMemo, useState } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import { Link } from "react-router-dom";
import AppBridgeCTA from "./AppBridgeCTA";
import NewsletterInline from "./NewsletterInline";

interface Props {
  content: string;
  source: string;
  isBrokerPost?: boolean;
}

// Split markdown roughly in half on a section boundary to inject a mid-post CTA.
function splitForMidCTA(md: string): [string, string] {
  const parts = md.split(/\n(?=## )/);
  if (parts.length < 3) return [md, ""];
  const mid = Math.ceil(parts.length / 2);
  return [parts.slice(0, mid).join("\n"), parts.slice(mid).join("\n")];
}

const components: Components = {
  a: ({ href, children, ...props }) => {
    if (href && href.startsWith("/")) {
      return (
        <Link to={href} className="text-primary underline underline-offset-2 hover:opacity-80">
          {children}
        </Link>
      );
    }
    return (
      <a
        href={href}
        target={href?.startsWith("http") ? "_blank" : undefined}
        rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
        className="text-primary underline underline-offset-2 hover:opacity-80"
        {...props}
      >
        {children}
      </a>
    );
  },
  h2: ({ children, ...props }) => (
    <h2 className="text-2xl md:text-3xl font-bold text-foreground mt-12 mb-4 scroll-mt-24" {...props}>
      {children}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 className="text-xl md:text-2xl font-bold text-foreground mt-8 mb-3 scroll-mt-24" {...props}>
      {children}
    </h3>
  ),
  p: ({ children }) => (
    <p className="text-foreground/85 leading-relaxed my-4">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc list-outside ps-6 my-4 space-y-2 text-foreground/85">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-outside ps-6 my-4 space-y-2 text-foreground/85">{children}</ol>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-6 border-s-4 border-primary/40 bg-primary/5 ps-5 pe-4 py-3 rounded-e-lg italic text-foreground/80">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="my-6 overflow-x-auto rounded-xl border border-border">
      <table className="w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-primary/5 text-foreground font-semibold">{children}</thead>
  ),
  th: ({ children }) => <th className="text-start px-4 py-3 border-b border-border">{children}</th>,
  td: ({ children }) => (
    <td className="px-4 py-3 border-b border-border/60 text-foreground/85">{children}</td>
  ),
  code: ({ children }) => (
    <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-mono text-foreground">
      {children}
    </code>
  ),
  hr: () => <hr className="my-10 border-border" />,
};

const MarkdownRenderer = ({ content, source, isBrokerPost = false }: Props) => {
  const [first, second] = useMemo(() => splitForMidCTA(content), [content]);

  return (
    <div className="prose-piefolio">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug, [rehypeAutolinkHeadings, { behavior: "wrap" }]]}
        components={components}
      >
        {first}
      </ReactMarkdown>

      {second && (isBrokerPost ? <AppBridgeCTA source={source} /> : null)}

      {second && (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeSlug, [rehypeAutolinkHeadings, { behavior: "wrap" }]]}
          components={components}
        >
          {second}
        </ReactMarkdown>
      )}

      <NewsletterInline source={source} />
    </div>
  );
};

export default MarkdownRenderer;
